/**
 * Pricing-related types shared between backend and frontend
 */

// ─── SPA enums ───────────────────────────────────────────────────────────────

export enum SpaQuoteType {
  ShipAndDebit = "ShipAndDebit",
  StandardSpa = "StandardSpa",
  Promotional = "Promotional",
  Rebate = "Rebate",
  Other = "Other",
}

// ─── SPA Request enums ───────────────────────────────────────────────────────

export enum SpaRequestStatus {
  Draft = "draft",
  PendingApproval = "pending_approval",
  InProgress = "in_progress",
  Done = "done",
}

export enum SpaUrgency {
  Low = "low",
  Medium = "medium",
  High = "high",
}

export enum SpaChangeType {
  Margins = "margins",
  Dates = "dates",
  AddItems = "add_items",
}

// ─── Price sheet validation (existing) ───────────────────────────────────────

/**
 * Status of price validation check against ERP list price
 */
export enum PriceValidationStatus {
  /** PO price matches or exceeds list price */
  STANDARD_PRICE = "STANDARD_PRICE",
  /** PO price matches approved discount */
  APPROVED_DISCOUNT = "APPROVED_DISCOUNT",
  /** PO price below expected (discount or list) price */
  UNAPPROVED_DISCOUNT = "UNAPPROVED_DISCOUNT",
  /** No list price available from ERP */
  NO_LIST_PRICE = "NO_LIST_PRICE",
}

/**
 * Result of a price validation check for a line item
 */
export interface PriceValidationResult {
  /** Status of the price check */
  status: PriceValidationStatus;
  /** Unit price from the purchase order */
  poUnitPrice: number;
  /** List price from ERP, null if not available */
  listPrice: number | null;
  /** Currency code from ERP, null if not available */
  currencyCode: string | null;
  /** Name of the price list used, null if not available */
  priceListName: string | null;
  /** Item number/SKU used for the query */
  itemNumber: string | null;
  /** Error message if the check failed */
  errorMessage?: string;
}

// ─── SPA renewal FINAL.xlsx ──────────────────────────────────────────────────────

/**
 * FINAL.xlsx's first 22 columns: the renewal's working columns (prior cost,
 * prior sell, margins, cost adder, …) ending with Division and LOADED TO.
 */
export const FINAL_WORKING_COLUMNS = [
  "Record ID",
  "QUOTE SPA",
  "QUOTE TYPE",
  "QUOTE PRODUCT",
  "QUOTE DESCRIPTION",
  "KEY VALUE ITEM",
  "Pricing Factor",
  "COST INCREASE",
  "PRIOR CORE COST NET",
  "PRIOR CORE COST COL",
  "PRIOR CORE COST DISC",
  "PRIOR SELL NET",
  "PRIOR SELL COL",
  "PRIOR SELL DISC",
  "FINAL PRIOR MARGINS",
  "FINAL PRIOR COST ADDER",
  "SALES BASED OFF ACCOUNT:",
  "12M ST1 OF HIGHEST SELLING ACCOUNT IN GROUP",
  "Market Segment",
  "Business Class",
  "Division",
  "LOADED TO",
] as const;

/**
 * FINAL.xlsx's last 82 columns: the AS400 upload columns in upload order,
 * fourteen of them under FINAL's own names (QUOTE START for START, NEW PO for
 * PO, QUOTE COST PER EACH for CORE COST NET, CHGDCCAD for COST ADD FLAG, …).
 */
export const FINAL_UPLOAD_COLUMNS = [
  "LOAD NUM",
  "Load Type",
  "Title",
  "Line2",
  "D2FLG",
  "Line3",
  "D3FLG",
  "QUOTE START",
  "QUOTE END",
  "Advertisement Type",
  "ADVFLG",
  "Order Type",
  "OTFLG",
  "AHCLAS",
  "AHCLFLG",
  "AHTERR",
  "AHTER",
  "Account",
  "AHCUS",
  "Group",
  "CHGCGRP",
  "REF",
  "REFFLG",
  "REQUISITION",
  "REQFLG",
  "NEW PO",
  "CPFLG",
  "AHRRQN",
  "RRQFLG",
  "ATXOVR",
  "EXCH",
  "EXCHFLG",
  "AHROLL",
  "AHSTAT",
  "HCCAD",
  "CHGHCCAD",
  "ADSEQ",
  "AS400 Item",
  "ADPCL",
  "ADPSCL",
  "MIN QTY",
  "MAX QTY",
  "SELL NET",
  "ADUCOL",
  "ADUADJ",
  "ADPONO",
  "ADRQNO",
  "ADSTAT",
  "STFLG",
  "ADCOST",
  "QUOTE COST PER EACH",
  "ADTXFD",
  "ADFTXI",
  "ADFTXC",
  "ADFLIC",
  "PTP",
  "PTT",
  "ADPLIC",
  "ADCADJ",
  "ADTCOL",
  "ADTADJ",
  "ADOCOL",
  "ADOADJ",
  "Roundup",
  "ADQ1",
  "ADQ2",
  "ADQ3",
  "ADQ4",
  "ADQP1",
  "ADQP2",
  "ADQP3",
  "ADQP4",
  "ADQPC1",
  "ADQPC2",
  "ADQPC3",
  "ADQPC4",
  "ADQPA1",
  "ADQPA2",
  "ADQPA3",
  "ADQPA4",
  "COST ADDER FIELD",
  "CHGDCCAD",
] as const;

/** The columns of the operator's FINAL.xlsx, in its order. */
export const FINAL_COLUMNS: readonly string[] = [
  ...FINAL_WORKING_COLUMNS,
  ...FINAL_UPLOAD_COLUMNS,
];
